import Cookies from "js-cookie";
import { dispatchCustomEvent } from "@/utils/customEvent/dispatchCustomEvent.ts";
import { SimplifiedBasketItem, BasketItem } from "./types";
import { Product } from "@JohnLewisPartnership/jl-analytics-event-stream";
import { BasketItemChildItem } from "./types.ts";

export const updateBasketAnalyticsObject = () => {
  const basketCookie = Cookies.get("cached_basket");

  const emptyBasketResponse = {
    product: [],
    subTotal: "0.00",
  };

  if (!basketCookie) {
    window.jlData = { ...window.jlData, basket: emptyBasketResponse };
    return;
  }

  const basketContents = basketCookie.split("^");

  basketContents.pop();
  const basketValue = (basketContents.pop()?.match(/\d+.\d+/) ?? [])[0];
  const newBasketObject = {
    product: basketContents.map((basketItem) => {
      const basketItemValues = basketItem.split("|");
      return {
        itemPrice: basketItemValues[4],
        productId: basketItemValues[1],
        qty: basketItemValues[5],
        services: [],
        sku: basketItemValues[3],
      };
    }),
    subTotal: `${basketValue}`,
  };

  window.jlData = { ...window.jlData, basket: newBasketObject };
};

export const updateExpressAnalytics = () => {
  dispatchCustomEvent("dataStateChange", {
    action: "global.interaction",
    type: "pdp",
    subType: "express checkout",
    additionalInfo: {},
    properties: {},
  });
};

export interface ProductInfo {
  productId: string;
  price?: number | string;
  title?: string;
  skuId: string;
  variantId: string;
  qty: number;
  childItems?: Array<{
    skuId: string;
    quantity?: number;
  }>;
}

const productPayload = (
  product: ProductInfo,
  isExpressCheckout: boolean,
  commerceItems: SimplifiedBasketItem[],
) => {
  const payload: Record<string, unknown> = {
    productId: product.productId,
    title: product.title,
    price: product.price,
    sku: product.skuId,
    variantId: product.variantId,
    qty: product.qty,
    services: product.childItems || [],
  };

  if (isExpressCheckout && commerceItems) {
    const addedItem = commerceItems.find(
      (item) => item.skuId === product.skuId,
    );

    payload.colour = addedItem?.variantTraits?.colour;
    payload.size = addedItem?.variantTraits?.size;
  }

  return payload;
};

export const addToBasketSuccessPayload = (
  product: ProductInfo,
  isExpressCheckout: boolean,
  commerceItems: SimplifiedBasketItem[],
) => {
  return {
    type: isExpressCheckout ? "shipfromstore" : "standard",
    buttonType: isExpressCheckout ? "expresscheckout" : "static",
    qtyRequested: product.qty,
    product: { ...productPayload(product, isExpressCheckout, commerceItems) },
  };
};

export const getChildItemsDataForNewAnalytics = (
  childItems?: BasketItemChildItem[],
) => {
  if (!childItems || childItems.length === 0) {
    return [];
  }

  return childItems.map((item) => ({
    productId: item.skuId,
    skuId: item.skuId,
    itemPrice: item.price,
    qty: item.quantity,
    name: item.name,
  }));
};

export const buildBasketUpdateDataFromBasket = (
  commerceItems: SimplifiedBasketItem[],
) => {
  return {
    products:
      commerceItems?.map((basketItem) => ({
        name: basketItem.title || "",
        quantity: parseInt(basketItem.quantity, 10) || 0,
        price: basketItem.price ?? 0,
        id: basketItem.id,
      })) || [],
    productCount: commerceItems?.length || 0,
    quantity:
      commerceItems?.reduce(
        (count, basketItem) => count + (parseInt(basketItem.quantity, 10) || 0),
        0,
      ) || 0,
  };
};

export const buildTrackEventProductData = (
  productId: string,
  title: string,
  quantity: number,
  skuId: string,
  addedItem: BasketItem,
): Product => {
  const priceNow: number = addedItem?.variantPrice
    ? Number.parseFloat(addedItem.variantPrice.now)
    : (addedItem?.price ?? 0);

  return {
    id: productId,
    name: title ?? "",
    quantity,
    sku: skuId,
    services: getChildItemsDataForNewAnalytics(addedItem?.childItems).map(
      (item) => ({
        id: item.skuId,
        sku: item.skuId,
        name: item.name,
        price: item.itemPrice.toString(),
      }),
    ),
    price: {
      now: priceNow,
      was: addedItem?.originalPrice,
    },
    brand: addedItem?.brand?.name,
    colour: addedItem?.variantTraits?.colour,
    promotionClassifications: addedItem?.detailedPromotionalMessages?.map(
      (message) => ({
        group: message.promotionGroup,
        type: message.promotionType,
      }),
    ),
    size: addedItem?.variantTraits?.size,
  };
};
